import { CreditCostMarker } from "@/components/features/Credits/CreditCostMarker";
import { TextButton } from "@/design-system/TextButton/TextButton";
import { cn } from "@/lib/utils";
import { evolutionsBuilderSelector } from "@/redux/evolutions/evolutionsBuilderSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { UnlockIcon } from "lucide-react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { EvolutionChainCard } from "./EvolutionChainCard";

interface EvolutionChainProps {
  evolutionChain: number[];
  /** Removes the step (and everything after it) from the chain. */
  onClickStep: (indexInChain: number) => void;
  onClickClearChain: () => void;
  isSticky?: boolean;
}

/**
 * The builder's Evolution Chain: the same card as the player page, editable.
 * Each step's label opens the chain sheet; its × truncates the chain there.
 * Once scrolled past, a header-less row of it joins the pinned player bar.
 */
export const EvolutionChain = ({
  evolutionChain,
  onClickStep,
  onClickClearChain,
  isSticky = false,
}: EvolutionChainProps) => {
  const { t } = useTranslation("evoBuilder");
  const { currentPathwayChain } = useSelector(evolutionsBuilderSelector);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const emptyHint = (
    <span className="shrink-0 whitespace-nowrap text-xs text-foreground-muted">
      {t("chain.emptyHint")}
    </span>
  );

  return (
    <EvolutionChainCard
      evolutionIds={evolutionChain}
      pathwayPath={currentPathwayChain}
      variant="editable"
      onRemoveStep={onClickStep}
      layout="scroll"
      animate
      compact={isSticky}
      // Pinned: rendered inside the header bar, so it drops its own card
      // chrome and reads as a row of that bar.
      className={cn(isSticky && "rounded-none border-0 bg-transparent p-0")}
      // Above phone width the hint sits where the chain will start; phones
      // keep it in the header.
      emptyHint={!isMobile && emptyHint}
      action={
        evolutionChain.length > 0 ? (
          <TextButton onClick={onClickClearChain}>
            {t("chain.clear")}
          </TextButton>
        ) : (
          isMobile && emptyHint
        )
      }
    />
  );
};

/**
 * Why the steps are covered.
 *
 * "login" — nobody is signed in, so there is no account to reveal them to.
 * "reveal" — signed in, and the backend withheld the chain ids for a player
 * already on an evo path. Revealing them is the metered action, hence the coin.
 */
export type ChainWallMode = "login" | "reveal";

export const ChainWall = ({
  mode,
  onClick,
}: {
  mode: ChainWallMode;
  onClick: () => void;
}) => {
  const { t } = useTranslation("evoBuilder");
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      className="flex h-full w-full cursor-pointer flex-row items-center justify-center gap-x-1.5 rounded-lg bg-white/10 text-base font-semibold text-foreground shadow-default-drop backdrop-blur-lg transition-colors duration-200 ease-out hover:bg-white/20"
    >
      {mode === "login" ? (
        <>
          <UnlockIcon size={16} className="text-foreground-muted" />
          <span>{t("chain.loginToUnlock")}</span>
        </>
      ) : (
        <>
          <span>{t("chain.reveal")}</span>
          {/*
            The only metered action whose price stays inside its button. This
            wall fills the chain area edge to edge, so there is no outside to
            hang the marker off — and it doesn't need one: the surface is a
            translucent grey, not the brand blue, which is the case where the
            coin already had contrast.

            Switching from a bare CreditIcon to the marker also puts the number
            on screen and, more importantly, subjects it to the silence rule —
            the raw icon rendered even while credits were still unmentioned.
          */}
          <CreditCostMarker featureId="evo-chains-club" className="ml-1" />
        </>
      )}
    </button>
  );
};
